import {toast} from '@wandb/weave/common/components/elements/Toast';
import WandbLoader from '@wandb/weave/common/components/WandbLoader';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  connectionToNodes,
  opArtifactId,
  opArtifactName,
  opArtifactProject,
  opArtifactRawTags,
  opArtifactType,
  opArtifactTypeName,
  opDict,
  opEntityName,
  opProjectEntity,
  opProjectName,
} from '@wandb/weave/core';
import {useValue} from '@wandb/weave/react';
import * as React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {
  useCreateArtifactCollectionTagAssignmentsMutation,
  useDeleteArtifactCollectionTagAssignmentsMutation,
  useEntityTagsQuery,
} from '../../../generated/graphql';
import {ENTITY_TAGS_QUERY} from '../../../state/graphql/entityTagsQuery';
import * as GQLTypes from '../../../types/graphql';
import {getErrMsgFromError, propagateErrorsContext} from '../../../util/errors';
import {useIsProjectReadOnly} from '../../Registries/common/hooks';
import {
  AddTagAssignmentButton,
  createAttributesString,
  mapGQLTags,
  matchTag,
  ModifiedTag,
  parseTag,
  TagAssignment,
  TagAttributes,
} from '../../TagAssignment';

const inputType = 'artifact' as const;
export type ConfigType = {};

export const ArtifactTagList = styled.div<{wrapList?: boolean}>`
  display: flex;
  ${props => (props.wrapList ? `flex-wrap: wrap; row-gap: 5px;` : ``)}
  overflow: auto;
`;

export type TagListProps = {
  showButton: boolean;
  showColor: boolean;
  tagSize: 'small' | 'medium' | 'large';
  wrap?: boolean;
  showLoader?: boolean;
};

const PanelArtifactTagList: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType> & TagListProps
> = props => {
  const artifactNode = props.input;
  const overviewValue = useValue(
    React.useMemo(() => {
      const projectNode = opArtifactProject({
        artifact: artifactNode,
      });
      return opDict({
        artifactTypeName: opArtifactTypeName({
          artifactType: opArtifactType({artifact: artifactNode}),
        }),
        collectionID: opArtifactId({
          artifact: artifactNode,
        }),
        collectionName: opArtifactName({
          artifact: artifactNode,
        }),
        entityName: opEntityName({
          entity: opProjectEntity({project: projectNode}),
        }),
        projectName: opProjectName({
          project: projectNode,
        }),
        rawTags: opArtifactRawTags({
          artifact: artifactNode,
        }),
      } as any);
    }, [artifactNode])
  );

  const currentTagsRaw: Array<{
    id: string;
    name: string;
    tagCategoryName: string;
    attributes: string;
  }> = React.useMemo(
    () => overviewValue.result?.rawTags ?? [],
    [overviewValue.result?.rawTags]
  );

  const currentTags = mapGQLTags(currentTagsRaw);

  const {
    data: entityData,
    loading: entityLoading,
    error: entityError,
  } = useEntityTagsQuery({
    variables: {
      entityName: overviewValue.result?.entityName,
    },
    // entityTags are only needed while rendering the list of tags
    // when a user tries to add a new tag. So the query result is not
    // needed if we're rendering the list without the 'add' button
    skip: !overviewValue.result?.entityName || !props.showButton,
  });
  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(
      overviewValue.result?.entityName ?? '',
      overviewValue.result?.projectName ?? ''
    );

  const entity = entityData?.entity;
  const allTags = React.useMemo(() => {
    const rawTags =
      entity?.tagCategories?.flatMap(tc => connectionToNodes(tc.tags)) ?? [];
    return mapGQLTags(rawTags);
  }, [entity]);

  if (
    overviewValue.loading ||
    overviewValue.result == null ||
    entityLoading ||
    isProjectReadOnlyLoading
  ) {
    if (props.showLoader) {
      return (
        <WandbLoader name="artifact-collection-tag-list" inline size="tiny" />
      );
    } else {
      return <></>;
    }
  }

  // We don't want to display the error message in case of the
  // entity query failing as they only list out existing tags on the collection
  if (entityError) {
    const errMsg = getErrMsgFromError(entityError);
    console.error(`Error retrieving entity tags; Msg: ${errMsg}`);
  }

  return (
    <PanelArtifactCollectionTagListInner
      {...props}
      refresh={overviewValue.refresh}
      entityName={overviewValue.result.entityName}
      projectName={overviewValue.result.projectName}
      artifactCollectionName={overviewValue.result.collectionName}
      currentTags={currentTags}
      allTags={allTags}
      showButton={props.showButton && !readOnly}
    />
  );
};

type ArtifactCollectionTagListInnerProps = {
  refresh: () => void;
  entityName: string;
  projectName: string;
  artifactCollectionName: string;
  currentTags: ModifiedTag[];
  allTags: ModifiedTag[];
};

export const PanelArtifactCollectionTagListInner: React.FC<
  ArtifactCollectionTagListInnerProps & TagListProps
> = props => {
  const [addTagAssignment] = useCreateArtifactCollectionTagAssignmentsMutation({
    context: propagateErrorsContext(),
    refetchQueries: [
      {
        query: ENTITY_TAGS_QUERY,
        variables: {entityName: props.entityName},
      },
    ],
  });

  const createTagAssignment = React.useCallback(
    async (tagInput: GQLTypes.Tag) => {
      try {
        const tagStr = tagInput.name;
        if (props.currentTags.some((t: ModifiedTag) => matchTag(t, tagStr))) {
          return Promise.resolve();
        }

        // Parse input string into tag and category
        const [tagCategoryName, tagName] = parseTag(tagStr);

        // Stringify Attributes
        const tagAttributes: TagAttributes = {
          color: tagInput.colorIndex,
          icon: tagInput.icon,
        };
        const attributesStr = createAttributesString(tagAttributes);
        await addTagAssignment({
          variables: {
            entityName: props.entityName,
            projectName: props.projectName,
            artifactCollectionName: props.artifactCollectionName,
            tags: [
              {
                tagCategoryName,
                tagName,
                attributes: attributesStr,
              },
            ],
          },
        });
        props.refresh();
      } catch (err) {
        console.error(err);
        toast(
          'Something went wrong when trying to create this tag. Please check for special characters.'
        );
      }
      return;
    },
    [addTagAssignment, props]
  );

  const [removeTagAssignment] =
    useDeleteArtifactCollectionTagAssignmentsMutation({
      context: propagateErrorsContext(),
      refetchQueries: [
        {
          query: ENTITY_TAGS_QUERY,
          variables: {entityName: props.entityName},
        },
      ],
    });

  const deleteTagAssignment = React.useCallback(
    async (tag: ModifiedTag) => {
      try {
        await removeTagAssignment({
          variables: {
            entityName: props.entityName,
            projectName: props.projectName,
            artifactCollectionName: props.artifactCollectionName,
            tags: [
              {
                tagCategoryName: tag.tagCategoryName,
                tagName: tag.name,
              },
            ],
          },
        });
        props.refresh();
      } catch (error) {
        console.error(error);
        toast('Something went wrong when trying to delete this tag.');
      }
      return;
    },
    [removeTagAssignment, props]
  );

  return (
    <ArtifactTagList wrapList={props.wrap}>
      {props.currentTags.map(tag => {
        return (
          <TagAssignment
            key={tag.tagCategoryName + tag.name}
            tag={tag}
            showColor={props.showColor}
            size={props.tagSize}
            canDelete={props.showButton}
            onDelete={() => deleteTagAssignment(tag)}
          />
        );
      })}
      {props.showButton && (
        <AddTagAssignmentButton
          assignedTags={props.currentTags}
          availableTags={props.allTags}
          onCreate={createTagAssignment}
        />
      )}
    </ArtifactTagList>
  );
};

export default PanelArtifactTagList;
